import { useEffect, useState } from 'react';
import { VyDialog } from '../../../../../../../../../../../components/new-design/feedback/VyDialog';
import { Autocomplete, Stack } from '@mui/material';
import { TextField } from '@mui/material';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import type { XeroProductPayload } from '@vy/api-definition';
import { useModifyExternalProduct } from '../../../../../../../../../../../hooks/requests/integrations';
import { runMutation } from '../../../../../../../../../../../utils/vy-mutation';
import { fromNullToUndefined } from '@vy/utils';
import { useQueryClient } from 'react-query';

type EditXeroProductDialogProps = {
  open: boolean;
  onClose: () => void;
  productId: string;
  externalProductId: string | undefined;
  xeroProducts: XeroProductPayload[];
};

export const EditXeroProductDialog = ({
  open,
  onClose,
  productId,
  externalProductId,
  xeroProducts,
}: EditXeroProductDialogProps) => {
  const modifyExternalProductMutation = useModifyExternalProduct(productId);
  const queryClient = useQueryClient();

  const [selectedXeroProductId, setSelectedXeroProductId] = useState<string | null>(
    externalProductId ?? null,
  );

  useEffect(() => {
    if (open) {
      setSelectedXeroProductId(externalProductId ?? null);
    }
  }, [open, externalProductId]);

  const selectedXeroProduct = xeroProducts.find(
    (product) => product.id === selectedXeroProductId,
  );

  const handleSubmit = async () => {
    await runMutation(
      modifyExternalProductMutation,
      {
        externalId: fromNullToUndefined(selectedXeroProductId),
        integration: INTEGRATION_PROVIDERS.xero,
        productId,
      },
      {
        pending: 'Updating product...',
        success: 'Product updated successfully',
        error: 'Failed to update product',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === 'invoice-products-integrations',
          });
          onClose();
        },
      },
    );
  };

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label="Edit Xero Product"
      cancelButtonText="Cancel"
      ctaText="Save"
      onSubmit={handleSubmit}
    >
      <Stack>
        <Autocomplete
          options={xeroProducts ?? []}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedXeroProduct}
          onChange={(event, value) => setSelectedXeroProductId(value?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Xero Product" />}
        />
      </Stack>
    </VyDialog>
  );
};
